import type { OrganizationDevice, OrganizationDeviceFilters } from '@/types/organization'
import {
	type BulkDeviceStatusResponse,
	type BulkDeviceStatusTarget,
	type ColumnFilterOptionsResult,
	type CreateDevicePayload,
	type DeviceCertificateResponse,
	type DeviceConfigurationsDownload,
	type DeviceDataDownload,
	type DeviceDataDownloadRequest,
	type DeviceFilterOption,
	type DeviceIdsWithFields,
	type DeviceSearchFilters,
	type DeviceSearchSortBy,
	DeviceStatus,
	type FilterOptionsApiResponse,
	type GetColumnFilterOptionsArgs,
	type MetadataKeyResponse,
	type ProvisionDevicesBulkRequest,
	type ProvisionedDeviceBulkItem,
	type SearchDeviceIdsResponse,
	type SearchDeviceResponse,
	type SortOrder,
	transformFilterOptionsResponse,
	validateDeviceDataDownloadRequest,
} from '../deviceApi'
import { demoApi } from './db'

async function updateDemoDeviceStatuses(
	deviceIds: string[],
	active: boolean,
	tenantId?: string,
): Promise<BulkDeviceStatusResponse> {
	await Promise.all(
		deviceIds.map(id =>
			tenantId
				? demoApi.patchNoTenant(`/v3/devices/${encodeURIComponent(id)}/update`, { active, tenant_id: tenantId })
				: demoApi.put(`/v1/devices/${encodeURIComponent(id)}`, {
						status: active ? DeviceStatus.Active : DeviceStatus.Inactive,
					}),
		),
	)
	return { updated: deviceIds.length }
}

export const deviceApi = {
	generateDeviceCertificates: async (
		deviceId: number | string,
		lifetime: number | null = 315360000,
		tenantId?: string,
	): Promise<DeviceCertificateResponse> => {
		if (tenantId) {
			return demoApi.postNoTenant<DeviceCertificateResponse>('/v3/devices/generate-certificates', {
				device_id: String(deviceId),
				tenant_id: tenantId,
				lifetime,
			})
		}
		return demoApi.get<DeviceCertificateResponse>(`/v1/devices/${encodeURIComponent(String(deviceId))}/cert`)
	},

	getDeviceFilterOptions: async (
		_tenantId?: string,
		status: DeviceStatus = DeviceStatus.Active,
	): Promise<DeviceFilterOption[]> => {
		const response = await demoApi.post<FilterOptionsApiResponse>('/v3/devices/filter-options', { status })
		return transformFilterOptionsResponse(response)
	},

	getDeviceColumnFilterOptions: async ({
		column,
		page = 1,
		perPage = 1000,
		search,
		status = DeviceStatus.Active,
		filters,
		organizationId,
	}: GetColumnFilterOptionsArgs): Promise<ColumnFilterOptionsResult> => {
		const trimmedSearch = search?.trim()
		const data: Record<string, unknown> = { status, page, per_page: perPage, column }
		if (trimmedSearch) {
			data.search = trimmedSearch
		}
		if (filters !== undefined && filters !== null) {
			data.filters = filters
		}
		const url = organizationId
			? `/v3/organizations/${encodeURIComponent(organizationId)}/devices/filter-options`
			: '/v3/devices/filter-options'
		const response = await demoApi.post<{ values?: (string | number | boolean | null)[]; count?: number }>(url, data)
		const values: (string | number)[] = []
		for (const value of response.values ?? []) {
			if (value === null || value === undefined) {
				continue
			}
			values.push(typeof value === 'boolean' ? String(value) : value)
		}
		return { values, count: response.count ?? 0 }
	},

	downloadDeviceData: async (request: DeviceDataDownloadRequest, _tenantId?: string): Promise<DeviceDataDownload> => {
		validateDeviceDataDownloadRequest(request)
		const blob = await demoApi.postBlob('/v3/devices/download', request)
		return { blob, filename: 'devices.csv' }
	},

	downloadDeviceConfigurations: async (
		_deviceIds: string[],
		_tenantId?: string,
	): Promise<DeviceConfigurationsDownload> => {
		const blob = new Blob([], { type: 'application/zip' })
		return { blob, filename: 'device-configurations.zip' }
	},

	/**
	 * Map V3 createDevice to demo API V3 endpoint, which proxies to consoleD and
	 * stores mapping in Neon (devices.console_id).
	 */
	createDevice: async (payload: CreateDevicePayload, tenantId?: string): Promise<DeviceCertificateResponse> => {
		const options: RequestInit = {}
		if (tenantId) {
			options.headers = {
				'x-bytebeam-tenant': tenantId,
			}
		}
		return demoApi.post('/v3/devices', payload, options)
	},

	/**
	 * POST /v3/devices/bulk — same tenant header pattern as createDevice.
	 */
	bulkCreateDevices: async (
		params: ProvisionDevicesBulkRequest,
		tenantId: string,
	): Promise<ProvisionedDeviceBulkItem[]> => {
		const needsLifetime = params.generate_mtls_creds || params.generate_http_creds
		const body: Record<string, unknown> = {
			count: params.count,
			generate_mtls_creds: params.generate_mtls_creds,
			generate_http_creds: params.generate_http_creds,
			// TODO(withHttpServer): Plumb `with_http_server` (bool) on create/bulk. Response-shaping only: if true, each device in the response includes `http_server` (value from `DEVICE_API_DOMAIN` / `device_api_domain`); if false or omitted, the key is not returned. This is the HTTP ingest host for building stream submit URLs, not a request body field for the domain. Independent of `generate_http_creds` (which only adds `http_credentials`).
			// with_http_server: params.with_http_server,
		}
		if (needsLifetime) {
			if (params.lifetime === undefined) {
				throw new Error('lifetime is required when generating mTLS or HTTP credentials')
			}
			body.lifetime = params.lifetime
		}
		return demoApi.post('/v3/devices/bulk', body, {
			headers: { 'x-bytebeam-tenant': tenantId },
		})
	},

	/**
	 * Map V3 getDevicesV3 to V1/V2 getDevices.
	 * When tenantId is provided, calls demo API with x-bytebeam-tenant header (per-tenant device list).
	 */
	getDevicesV3: async (
		page = 1,
		perPage = 10,
		status: DeviceStatus = DeviceStatus.All,
		tenantId?: string,
	): Promise<SearchDeviceResponse> => {
		const path = `/v2/devices?page=${page}&limit=${perPage}&status=${status}`
		if (tenantId) {
			return demoApi.getWithTenantId(path, tenantId)
		}
		return demoApi.get(path)
	},

	/**
	 * Get all device IDs the current user can access (V3 API)
	 * GET /v3/devices/ids
	 */
	getDeviceIds: async (status: DeviceStatus = DeviceStatus.All, tenantId?: string): Promise<string[]> => {
		const path = `/v3/devices/ids?status=${status}`
		const res = tenantId
			? await demoApi.getWithTenantId<{ ids: string[] }>(path, tenantId)
			: await demoApi.get<{ ids: string[] }>(path)
		return res.ids ?? []
	},

	/**
	 * Get device ids matching a search filter (V3 API)
	 * POST /v3/devices/ids — returns first page of ids and total count.
	 */
	searchDeviceIds: async ({
		status = DeviceStatus.All,
		filters,
	}: {
		status?: DeviceStatus
		filters?: DeviceSearchFilters | null
		signal?: AbortSignal
	}): Promise<SearchDeviceIdsResponse> => {
		const data: Record<string, unknown> = { status }
		if (filters !== undefined && filters !== null) {
			data.filters = filters
		}
		const res = await demoApi.post<{ ids?: string[]; count?: number }>('/v3/devices/ids', data)
		const ids = Array.isArray(res.ids) ? res.ids : []
		const count = typeof res.count === 'number' ? res.count : ids.length
		return { ids, count }
	},

	searchDeviceIdsWithFields: async ({
		status = DeviceStatus.All,
		filters,
		metadataFields,
		stateFields,
	}: {
		status?: DeviceStatus
		filters?: DeviceSearchFilters | null
		metadataFields?: string[]
		stateFields?: string[]
		signal?: AbortSignal
	}): Promise<DeviceIdsWithFields[]> => {
		const data: Record<string, unknown> = { status }
		if (filters !== undefined && filters !== null) {
			data.filters = filters
		}
		if (metadataFields && metadataFields.length > 0) {
			data.metadata_fields = metadataFields
		}
		if (stateFields && stateFields.length > 0) {
			data.state_fields = stateFields
		}
		const res = await demoApi.post<{ devices?: DeviceIdsWithFields[] }>('/v3/devices/ids', data)
		return res.devices ?? []
	},

	activateDevicesBulkV3: async (
		target: BulkDeviceStatusTarget,
		tenantId?: string,
	): Promise<BulkDeviceStatusResponse> => {
		const ids = Array.isArray(target) ? target : []
		return updateDemoDeviceStatuses(ids, true, tenantId)
	},

	deactivateDevicesBulkV3: async (
		target: BulkDeviceStatusTarget,
		tenantId?: string,
	): Promise<BulkDeviceStatusResponse> => {
		const ids = Array.isArray(target) ? target : []
		return updateDemoDeviceStatuses(ids, false, tenantId)
	},

	/**
	 * Single Device Metadata Update (V3 API)
	 * PATCH /v3/devices/{id}/update
	 * TODO: needs to add support for this endpoint
	 */
	updateDeviceMetadataV3: async (
		deviceId: string,
		metadata: Record<string, string | null>,
		tenantId?: string,
	): Promise<void> => {
		await demoApi.patch(
			`/v3/devices/${encodeURIComponent(deviceId)}/update`,
			{ metadata },
			tenantId
				? {
						headers: {
							'x-bytebeam-tenant': tenantId,
						},
					}
				: undefined,
		)
	},

	/**
	 * Bulk Device Metadata Update (V3 API)
	 * POST /v3/devices/metadata-bulk
	 */
	updateDeviceMetadataBulkV3: async (file: File, tenantId?: string): Promise<{ updated: number }> => {
		const form = new FormData()
		form.append('file', file)
		const currentTenant = (await import('@/stores/authStore')).useAuthStore.getState().currentTenant
		const res = await fetch('/demo-api/v3/devices/metadata-bulk', {
			method: 'POST',
			headers: { 'x-bytebeam-tenant': tenantId || currentTenant || '' },
			body: form,
		})
		if (!res.ok) {
			const err = await res.json().catch(() => ({ error: res.statusText }))
			throw new Error(err.error || `Demo API error: ${res.status}`)
		}
		return res.json()
	},

	getAllDeviceMetadataKeys: async (tenantId?: string): Promise<MetadataKeyResponse[]> => {
		const response: MetadataKeyResponse[] = tenantId
			? await demoApi.getWithTenantId('/v3/metadata-keys', tenantId)
			: await demoApi.get('/v3/metadata-keys')
		return response ?? []
	},

	createMetadataKey: async (name: string): Promise<{ status: string }> =>
		demoApi.post(`/v3/metadata-keys/${encodeURIComponent(name)}`),

	updateMetadataKey: async (name: string, newName: string): Promise<{ status: string }> =>
		demoApi.post(`/v3/metadata-keys/${encodeURIComponent(name)}/rename/${encodeURIComponent(newName)}`),

	deleteMetadataKey: async (name: string): Promise<{ status: string }> =>
		demoApi.delete(`/v3/metadata-keys/${encodeURIComponent(name)}`),

	getOrganizationDevices: async (
		organizationId: string,
		filters?: OrganizationDeviceFilters,
		page = 1,
		perPage = 20,
	): Promise<{ devices: OrganizationDevice[]; count: number }> => {
		const query = new URLSearchParams({
			page: String(page),
			per_page: String(perPage),
		})
		if (filters?.tenant_id) {
			query.set('tenant', filters.tenant_id)
		}
		const res = await demoApi.getNoTenant<{ result: OrganizationDevice[]; count: number }>(
			`/organizations/${encodeURIComponent(organizationId)}/devices?${query.toString()}`,
		)
		const devices = (res.result ?? []).map(d => ({
			...d,
			status:
				d.status ||
				((d as OrganizationDevice & { active?: boolean }).active === false
					? DeviceStatus.Inactive
					: DeviceStatus.Active),
		}))
		return { devices, count: res.count ?? 0 }
	},

	searchOrganizationDevices: async (
		organizationId: string,
		searchParams: {
			filters?: DeviceSearchFilters | null
			page?: number
			perPage?: number
			sortBy?: DeviceSearchSortBy
			sortOrder?: SortOrder
			status?: DeviceStatus
			tenant?: string
		},
	): Promise<{ devices: OrganizationDevice[]; count: number }> => {
		const payload = {
			page: searchParams.page ?? 1,
			per_page: searchParams.perPage ?? 20,
			status: searchParams.status ?? DeviceStatus.Active,
			...(searchParams.filters !== undefined && searchParams.filters !== null ? { filters: searchParams.filters } : {}),
			...(searchParams.sortBy !== undefined ? { sort_by: searchParams.sortBy } : {}),
			...(searchParams.sortOrder !== undefined ? { sort_order: searchParams.sortOrder } : {}),
		}
		const query = searchParams.tenant ? `?tenant=${encodeURIComponent(searchParams.tenant)}` : ''
		const res = await demoApi.postNoTenant<SearchDeviceResponse>(
			`/organizations/${encodeURIComponent(organizationId)}/devices/search${query}`,
			payload,
		)
		return {
			devices: (res.devices ?? []).map(d => ({
				...(d as unknown as OrganizationDevice),
				status:
					(d as unknown as OrganizationDevice).status ||
					((d as unknown as OrganizationDevice & { active?: boolean }).active === false ? 'inactive' : 'active'),
			})),
			count: res.count ?? 0,
		}
	},

	getOrgDeviceFilterOptions: async (organizationId: string, status: DeviceStatus = DeviceStatus.Active) => {
		const response = await demoApi.postNoTenant<FilterOptionsApiResponse>(
			`/organizations/${encodeURIComponent(organizationId)}/devices/filter-options`,
			{ status },
		)
		return transformFilterOptionsResponse(response)
	},
	getOrganizationDeviceIds: async (
		_organizationId: string,
		_status: DeviceStatus = DeviceStatus.All,
	): Promise<string[]> => {
		// TODO: update demo to have endpoint that returns ids of all devices at org level
		return []
	},

	// TODO: demo mock server has no filtered ids route yet. Throws so the page-level caller's
	// catch block falls back to loaded-rows-only selection (same as a real fetch failure).
	searchOrganizationDeviceIds: async (_params: {
		organizationId: string
		status?: DeviceStatus
		filters?: DeviceSearchFilters | null
		signal?: AbortSignal
	}): Promise<{ rowIds: string[]; pairs: { id: string; tenant_id: string; uid: number | null }[] }> => {
		throw new Error('Select-all across pages is not supported in demo mode yet')
	},
	getDeviceAllColumnFilterOptions: async (_params: {
		search: string
		perPage?: number
		status?: DeviceStatus
		filters?: DeviceSearchFilters | null
		tenantId?: string
		organizationId?: string
		signal?: AbortSignal
	}) => {
		return []
	},
}
